/**
 * FinancialAnalysis — project-based sell-vs-hold analyzer.
 *
 * Visually matches the Deal Underwriter so both tools feel like part of the
 * same Analysis Suite. Same underlying calculation logic as the previous
 * version (local sell math; backend rent math) — only the presentation
 * changed: clean white cards, sticky right-column KPIs + Sources & Uses
 * table, shadcn Tabs, Lucide icons (no emoji), no full-width gradient
 * header (the Suite wraps the page now).
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Building2, Home, DollarSign, FileText, Wrench, Landmark,
  AlertTriangle, Calculator, Banknote, TrendingUp, RefreshCw, CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import {
  NumInput, Section, KpiTile, fmt, fmtPct,
} from '@/components/analysis/AnalysisPrimitives';

// ---- formatters & primitives now imported from AnalysisPrimitives ----

function FinancialAnalysis({ user }) {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [analysisTab, setAnalysisTab] = useState('sell');
  const [rentResults, setRentResults] = useState(null);

  // ---- inputs (identical shape to old version so backend stays happy) ----
  const [inputs, setInputs] = useState({
    sale_price: 0,
    agent_commission_percent: 6,
    title_settlement_fee: 0,
    title_abstracting_fee: 0,
    home_warranty: 0,
    recording_fees_sale: 0,
    transfer_tax: 0,
    admin_compliance_fee: 0,
    seller_paid_buyer_costs: 0,
    loan_payoff_amount: 0,
    loan_origination_fee: 0,
    title_insurance_purchase: 0,
    appraisal_fee: 0,
    inspection_fee: 0,
    recording_fees_purchase: 0,
    land_cost: 0,
    construction_cost: 0,
    total_project_cost: 0,
    holding_costs: 0,
    interest_expense: 0,
    property_taxes_during_construction: 0,
    insurance_during_construction: 0,
    property_value: 0,
    monthly_rental_income: 0,
    annual_insurance: 0,
    annual_maintenance: 0,
    annual_property_tax: 0,
    management_fee_percent: 10,
    annual_reserves: 0,
    misc_expenses: 0,
    first_mortgage_amount: 0,
    first_mortgage_rate: 7,
    first_mortgage_term_years: 30,
    second_mortgage_amount: 0,
    second_mortgage_rate: 10,
    second_mortgage_interest_only: false,
  });
  const set = (patch) => setInputs((p) => ({ ...p, ...patch }));

  // ---- load projects ----
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setIsLoading(true);
        const response = await apiService.getProjects(user.id);
        if (alive) setProjects(response.data || []);
      } catch (err) {
        if (alive) toast.error('Failed to load projects');
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  // ---- when project changes, seed budget/value ----
  // ---- comprehensive project → inputs sync ----
  // Pulls every relevant cost/loan field from the live project record so the
  // analyzer reflects actual portfolio numbers instead of zeros. The user can
  // still override any field; the "Resync" button restores from project.
  const syncFromProject = (proj) => {
    if (!proj) return;
    const purchaseHome = Number(proj.purchase_price_home) || 0;
    const purchaseLand = Number(proj.purchase_price_land) || 0;
    const acqClosing = Number(proj.acquisition_closing_costs) || 0;
    const construction = Number(proj.construction_budget) || 0;
    const contingency = Number(proj.contingency_budget) || 0;
    const insurance = Number(proj.insurance) || 0;
    const drawn = Number(proj.amount_drawn) || 0;
    const principal = Number(proj.loan_principal_amount) || 0;
    const rate = Number(proj.loan_interest_rate) || 0;
    const periodMonths = Number(proj.loan_period_months) || 0;
    const dailyInterest = Number(proj.daily_interest_current) || 0;

    // Estimate interest expense to date using average-balance approximation:
    //   avg_balance × rate × years_active.
    // Most projects start with a small draw and grow — using current
    // daily_interest * days_active would over-state. Average balance gives a
    // defensible mid-point. User can override the field if they track exact.
    let interestExpenseEstimate = 0;
    if (proj.start_date && drawn > 0 && rate > 0) {
      const startMs = new Date(proj.start_date).getTime();
      const daysActive = Math.max(0, Math.floor((Date.now() - startMs) / (1000 * 60 * 60 * 24)));
      const avgBalance = drawn / 2;
      interestExpenseEstimate = (avgBalance * (rate / 100) * (daysActive / 360));
    } else if (dailyInterest > 0 && proj.start_date) {
      // Fallback: daily_interest × half the days_active (linear-draw assumption)
      const startMs = new Date(proj.start_date).getTime();
      const daysActive = Math.max(0, Math.floor((Date.now() - startMs) / (1000 * 60 * 60 * 24)));
      interestExpenseEstimate = dailyInterest * (daysActive / 2);
    }

    setInputs((prev) => ({
      ...prev,
      // ----- Sell-mode fields -----
      land_cost: purchaseLand || prev.land_cost,
      construction_cost: (construction + contingency) || prev.construction_cost,
      total_project_cost: Number(proj.total_budget) || 0,
      // Acquisition closing — lumped into loan_origination_fee slot when we
      // don't have a category breakdown on the project.
      loan_origination_fee: acqClosing || prev.loan_origination_fee,
      // Current loan balance is what you'd pay off at sale
      loan_payoff_amount: drawn ? Math.round(drawn) : (principal ? Math.round(principal) : prev.loan_payoff_amount),
      // Holding costs already on the project
      insurance_during_construction: insurance || prev.insurance_during_construction,
      interest_expense: Math.round(interestExpenseEstimate) || prev.interest_expense,
      // Pre-fill estimated sale price too if the project has one
      sale_price: Number(proj.estimated_sale_price) || prev.sale_price,
      // Acquisition purchase price (home) — surface as Construction Cost
      // companion when no land breakdown exists (rare); leave alone otherwise.
      // ----- Rent-mode fields -----
      property_value: Number(proj.estimated_sale_price) || Number(proj.total_budget) || 0,
      first_mortgage_amount: principal || prev.first_mortgage_amount,
      first_mortgage_rate: rate || prev.first_mortgage_rate,
      first_mortgage_term_years: periodMonths ? Math.round(periodMonths / 12) : prev.first_mortgage_term_years,
    }));
  };

  useEffect(() => {
    if (selectedProjectId === 'new') {
      setSelectedProject({ name: 'Quick Analysis', total_budget: 0, status: 'N/A' });
    } else if (selectedProjectId && projects.length > 0) {
      const proj = projects.find((p) => p.id === selectedProjectId);
      setSelectedProject(proj || null);
      syncFromProject(proj);
    }
    setRentResults(null);
  }, [selectedProjectId, projects]);

  // ---- LIVE Sell calculation (no button click — mirrors Underwrite UX) ----
  const sellResults = useMemo(() => {
    const salePrice = inputs.sale_price || 0;
    const agentCommission = salePrice * (inputs.agent_commission_percent / 100);
    const totalSellingCosts =
      agentCommission +
      (inputs.title_settlement_fee || 0) +
      (inputs.title_abstracting_fee || 0) +
      (inputs.home_warranty || 0) +
      (inputs.recording_fees_sale || 0) +
      (inputs.transfer_tax || 0) +
      (inputs.admin_compliance_fee || 0) +
      (inputs.seller_paid_buyer_costs || 0);

    const totalAcquisitionCosts =
      (inputs.loan_origination_fee || 0) +
      (inputs.title_insurance_purchase || 0) +
      (inputs.appraisal_fee || 0) +
      (inputs.inspection_fee || 0) +
      (inputs.recording_fees_purchase || 0);

    const totalProjectCost =
      inputs.total_project_cost ||
      ((inputs.land_cost || 0) + (inputs.construction_cost || 0)) ||
      selectedProject?.total_budget ||
      0;

    const totalHolding =
      (inputs.holding_costs || 0) +
      (inputs.interest_expense || 0) +
      (inputs.property_taxes_during_construction || 0) +
      (inputs.insurance_during_construction || 0);

    const totalAllInCost = totalProjectCost + totalAcquisitionCosts + totalHolding;
    const netProceeds = salePrice - totalSellingCosts - (inputs.loan_payoff_amount || 0);
    const netProfit = salePrice - totalSellingCosts - totalAllInCost;
    const roi = totalAllInCost > 0 ? (netProfit / totalAllInCost) * 100 : 0;
    const breakEvenSale = totalAllInCost / (1 - (inputs.agent_commission_percent / 100));

    return {
      salePrice,
      agentCommission,
      totalSellingCosts,
      totalAcquisitionCosts,
      totalProjectCost,
      totalHolding,
      totalAllInCost,
      netProceeds,
      netProfit,
      roi,
      breakEvenSale,
    };
  }, [inputs, selectedProject]);

  // ---- Rent analysis (still backend, button-triggered) ----
  const handleCalculateRent = async () => {
    if (!selectedProjectId || selectedProjectId === 'new') {
      toast.info('Build-to-Rent analysis requires a real project. Select one above.');
      return;
    }
    setIsCalculating(true);
    try {
      const requestData = { analysis_type: 'rent', ...inputs };
      const response = await apiService.calculateFinancialAnalysis(selectedProjectId, requestData);
      setRentResults(response.data);
      toast.success('Rental analysis calculated');
    } catch (e) {
      console.error('rent calc error', e);
      toast.error('Failed to calculate rental analysis');
    } finally {
      setIsCalculating(false);
    }
  };

  // ---- Empty state when no project selected ----
  const emptyState = (
    <Card className="p-10 text-center">
      <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-3" />
      <h3 className="text-sm font-semibold text-slate-900 mb-1">Select a Project to Begin</h3>
      <p className="text-xs text-slate-500 max-w-md mx-auto">Choose a project from the dropdown above, or pick <strong className="text-blue-600">Quick Analysis</strong> to model numbers without saving them to a project.</p>
    </Card>
  );

  return (
    <div className="px-4 sm:px-6 py-5 max-w-[1500px] mx-auto" data-testid="financial-analysis-page">
      {/* Header — matches Deal Underwriter style */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">Project Analysis</p>
          <h2 className="text-xl font-bold text-slate-900">Sell vs. Hold</h2>
          {selectedProject && (
            <p className="text-xs text-slate-500 mt-0.5">
              {selectedProjectId === 'new' ? (
                <span className="text-blue-600 font-medium">Quick Analysis — no project selected</span>
              ) : (
                <>
                  <span className="font-medium text-slate-700">{selectedProject.name}</span>
                  {' · '}Budget {fmt(selectedProject.total_budget || 0)}
                  {selectedProject.status && ` · ${selectedProject.status}`}
                </>
              )}
            </p>
          )}
          {selectedProjectId && selectedProjectId !== 'new' && (
            <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
              <CheckCircle2 className="w-3 h-3" />
              Costs auto-synced from project
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selectedProjectId && selectedProjectId !== 'new' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => syncFromProject(selectedProject)}
              className="h-9"
              title="Pull the latest costs, draws, and interest from this project"
              data-testid="resync-from-project"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Resync
            </Button>
          )}
          <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
            <SelectTrigger className="w-[260px] h-9" data-testid="project-selector">
              <SelectValue placeholder={isLoading ? 'Loading projects…' : 'Select a project'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new" className="text-blue-600 font-medium">Quick Analysis (no project)</SelectItem>
              {projects.length > 0 && projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name} — {fmt(p.total_budget || 0)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!selectedProjectId ? (
        emptyState
      ) : (
        <>
          {/* Mode tabs */}
          <Tabs value={analysisTab} onValueChange={setAnalysisTab} className="mb-4">
            <TabsList className="grid grid-cols-2 max-w-md">
              <TabsTrigger value="sell" data-testid="fa-mode-sell"><DollarSign className="w-4 h-4 mr-1" />Build-to-Sell</TabsTrigger>
              <TabsTrigger value="rent" data-testid="fa-mode-rent"><Home className="w-4 h-4 mr-1" />Build-to-Rent</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* ============================== INPUTS ============================== */}
            <div className="lg:col-span-7 space-y-3">
              {analysisTab === 'sell' && (
                <>
                  <Section title="Sale Information" icon={DollarSign}>
                    <div className="grid grid-cols-2 gap-3">
                      <NumInput label="Expected Sale Price *" value={inputs.sale_price} onChange={(v) => set({ sale_price: v })} placeholder="230,000" testid="inp-sale-price" />
                      <NumInput label="Agent Commission" value={inputs.agent_commission_percent} onChange={(v) => set({ agent_commission_percent: v })} prefix={null} suffix="%" testid="inp-commission-pct" />
                    </div>
                  </Section>

                  <Section title="Selling Costs" icon={FileText} subtitle="From your settlement statement when you SELL">
                    <div className="grid grid-cols-2 gap-3">
                      <NumInput label="Title / Settlement Fee" value={inputs.title_settlement_fee} onChange={(v) => set({ title_settlement_fee: v })} placeholder="480" testid="inp-title-settlement" />
                      <NumInput label="Title Abstracting" value={inputs.title_abstracting_fee} onChange={(v) => set({ title_abstracting_fee: v })} placeholder="480" testid="inp-title-abstract" />
                      <NumInput label="Home Warranty" value={inputs.home_warranty} onChange={(v) => set({ home_warranty: v })} placeholder="697" testid="inp-home-warranty" />
                      <NumInput label="Recording Fees / Stamps" value={inputs.recording_fees_sale} onChange={(v) => set({ recording_fees_sale: v })} placeholder="367" testid="inp-recording-sale" />
                      <NumInput label="Transfer Tax" value={inputs.transfer_tax} onChange={(v) => set({ transfer_tax: v })} placeholder="0" testid="inp-transfer-tax" />
                      <NumInput label="Admin / Compliance Fee" value={inputs.admin_compliance_fee} onChange={(v) => set({ admin_compliance_fee: v })} placeholder="395" testid="inp-admin-fee" />
                      <NumInput label="Seller-Paid Buyer Costs" value={inputs.seller_paid_buyer_costs} onChange={(v) => set({ seller_paid_buyer_costs: v })} placeholder="4,000" testid="inp-seller-paid" />
                      <NumInput label="Loan Payoff Amount" value={inputs.loan_payoff_amount} onChange={(v) => set({ loan_payoff_amount: v })} hint="Construction loan balance at close" placeholder="161,687" testid="inp-loan-payoff" />
                    </div>
                  </Section>

                  <Section title="Acquisition / Loan Closing Costs" icon={Landmark} subtitle="From your settlement statement when you GOT the loan" defaultOpen={false}>
                    <div className="grid grid-cols-2 gap-3">
                      <NumInput label="Loan Origination Fee" value={inputs.loan_origination_fee} onChange={(v) => set({ loan_origination_fee: v })} placeholder="2,000" testid="inp-origination" />
                      <NumInput label="Title Insurance" value={inputs.title_insurance_purchase} onChange={(v) => set({ title_insurance_purchase: v })} placeholder="1,500" testid="inp-title-insurance" />
                      <NumInput label="Appraisal Fee" value={inputs.appraisal_fee} onChange={(v) => set({ appraisal_fee: v })} placeholder="500" testid="inp-appraisal" />
                      <NumInput label="Inspection Fee" value={inputs.inspection_fee} onChange={(v) => set({ inspection_fee: v })} placeholder="400" testid="inp-inspection" />
                      <NumInput label="Recording Fees (Purchase)" value={inputs.recording_fees_purchase} onChange={(v) => set({ recording_fees_purchase: v })} placeholder="200" testid="inp-recording-purchase" />
                    </div>
                  </Section>

                  <Section title="Project & Holding Costs" icon={Wrench} subtitle="Land, build, and costs during construction">
                    <div className="grid grid-cols-2 gap-3">
                      <NumInput label="Land Cost" value={inputs.land_cost} onChange={(v) => set({ land_cost: v })} placeholder="50,000" testid="inp-land" />
                      <NumInput label="Construction Cost" value={inputs.construction_cost} onChange={(v) => set({ construction_cost: v })} placeholder="150,000" testid="inp-construction" />
                      <NumInput label="Total Project Cost (override)" value={inputs.total_project_cost} onChange={(v) => set({ total_project_cost: v })} placeholder={selectedProject?.total_budget ? `Auto: ${selectedProject.total_budget.toLocaleString()}` : 'Auto from land + construction'} testid="inp-total-project" />
                      <NumInput label="Interest Expense" value={inputs.interest_expense} onChange={(v) => set({ interest_expense: v })} hint="Total construction-loan interest paid" testid="inp-interest" />
                      <NumInput label="Property Taxes During Build" value={inputs.property_taxes_during_construction} onChange={(v) => set({ property_taxes_during_construction: v })} placeholder="635" testid="inp-tax-during" />
                      <NumInput label="Insurance During Build" value={inputs.insurance_during_construction} onChange={(v) => set({ insurance_during_construction: v })} placeholder="1,200" testid="inp-ins-during" />
                      <NumInput label="Other Holding Costs" value={inputs.holding_costs} onChange={(v) => set({ holding_costs: v })} hint="Utilities, HOA, etc." testid="inp-other-holding" />
                    </div>
                  </Section>
                </>
              )}

              {analysisTab === 'rent' && (
                <>
                  <Section title="Property Information" icon={Home}>
                    <div className="grid grid-cols-2 gap-3">
                      <NumInput label="Property Value" value={inputs.property_value} onChange={(v) => set({ property_value: v })} testid="inp-property-value" />
                      <NumInput label="Monthly Rental Income" value={inputs.monthly_rental_income} onChange={(v) => set({ monthly_rental_income: v })} placeholder="2,500" testid="inp-monthly-rent-fa" />
                    </div>
                  </Section>

                  <Section title="Operating Expenses (annual)" icon={Wrench}>
                    <div className="grid grid-cols-2 gap-3">
                      <NumInput label="Insurance" value={inputs.annual_insurance} onChange={(v) => set({ annual_insurance: v })} testid="inp-ann-insurance" />
                      <NumInput label="Maintenance & Repairs" value={inputs.annual_maintenance} onChange={(v) => set({ annual_maintenance: v })} testid="inp-ann-maintenance" />
                      <NumInput label="Property Tax" value={inputs.annual_property_tax} onChange={(v) => set({ annual_property_tax: v })} testid="inp-ann-prop-tax" />
                      <NumInput label="Management Fee" value={inputs.management_fee_percent} onChange={(v) => set({ management_fee_percent: v })} prefix={null} suffix="% of rent" testid="inp-mgmt-pct" />
                      <NumInput label="Annual Reserves" value={inputs.annual_reserves} onChange={(v) => set({ annual_reserves: v })} testid="inp-ann-reserves" />
                      <NumInput label="Misc. Expenses" value={inputs.misc_expenses} onChange={(v) => set({ misc_expenses: v })} testid="inp-misc" />
                    </div>
                  </Section>

                  <Section title="Financing" icon={Landmark}>
                    <div className="grid grid-cols-2 gap-3">
                      <NumInput label="1st Mortgage Amount" value={inputs.first_mortgage_amount} onChange={(v) => set({ first_mortgage_amount: v })} testid="inp-1st-amt" />
                      <NumInput label="1st Mortgage Rate" value={inputs.first_mortgage_rate} onChange={(v) => set({ first_mortgage_rate: v })} prefix={null} suffix="%" testid="inp-1st-rate" />
                      <NumInput label="1st Mortgage Term" value={inputs.first_mortgage_term_years} onChange={(v) => set({ first_mortgage_term_years: v })} prefix={null} suffix="yrs" testid="inp-1st-term" />
                      <NumInput label="2nd Mortgage Amount" value={inputs.second_mortgage_amount} onChange={(v) => set({ second_mortgage_amount: v })} testid="inp-2nd-amt" />
                      <NumInput label="2nd Mortgage Rate" value={inputs.second_mortgage_rate} onChange={(v) => set({ second_mortgage_rate: v })} prefix={null} suffix="%" testid="inp-2nd-rate" />
                      <div className="flex items-end pb-1">
                        <label className="flex items-center gap-2 text-xs text-slate-700">
                          <input
                            type="checkbox"
                            checked={inputs.second_mortgage_interest_only}
                            onChange={(e) => set({ second_mortgage_interest_only: e.target.checked })}
                            className="rounded"
                            data-testid="inp-2nd-io"
                          />
                          2nd Mortgage Interest-Only
                        </label>
                      </div>
                    </div>
                  </Section>

                  <Button onClick={handleCalculateRent} disabled={isCalculating} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="calculate-rent-btn">
                    <Calculator className="w-4 h-4 mr-2" />
                    {isCalculating ? 'Calculating…' : 'Calculate Rental Analysis'}
                  </Button>
                </>
              )}
            </div>

            {/* ============================== RESULTS ============================== */}
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-44 space-y-3">
                {analysisTab === 'sell' ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <KpiTile label="Net Profit" value={fmt(sellResults.netProfit)} color={sellResults.netProfit >= 0 ? 'emerald' : 'red'} />
                      <KpiTile label="ROI" value={fmtPct(sellResults.roi)} color={sellResults.roi >= 0 ? 'blue' : 'red'} />
                      <KpiTile label="Net Proceeds" value={fmt(sellResults.netProceeds)} color="slate" sub="After loan payoff" />
                      <KpiTile label="Break-Even Sale" value={fmt(sellResults.breakEvenSale)} color="amber" sub="To clear all costs" />
                    </div>

                    <Card className="p-4">
                      <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-600" />Sources & Uses</h3>
                      <table className="w-full text-xs">
                        <tbody>
                          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600">Sale Price</td><td className="py-1.5 text-right font-mono text-emerald-700">{fmt(sellResults.salePrice)}</td></tr>
                          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600 pl-3">− Agent Commission ({inputs.agent_commission_percent}%)</td><td className="py-1.5 text-right font-mono text-red-700">−{fmt(sellResults.agentCommission)}</td></tr>
                          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600 pl-3">− Other Selling Costs</td><td className="py-1.5 text-right font-mono text-red-700">−{fmt(sellResults.totalSellingCosts - sellResults.agentCommission)}</td></tr>
                          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600 pl-3">− Loan Payoff</td><td className="py-1.5 text-right font-mono text-red-700">−{fmt(inputs.loan_payoff_amount || 0)}</td></tr>
                          <tr className="border-t-2 border-slate-300"><td className="py-1.5 font-semibold">Net Proceeds at Closing</td><td className="py-1.5 text-right font-mono font-bold">{fmt(sellResults.netProceeds)}</td></tr>

                          <tr><td colSpan={2} className="pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Costs Already Sunk</td></tr>
                          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600">Project Cost</td><td className="py-1.5 text-right font-mono">{fmt(sellResults.totalProjectCost)}</td></tr>
                          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600">Loan Closing Costs</td><td className="py-1.5 text-right font-mono">{fmt(sellResults.totalAcquisitionCosts)}</td></tr>
                          <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600">Holding Costs (interest, tax, ins)</td><td className="py-1.5 text-right font-mono">{fmt(sellResults.totalHolding)}</td></tr>
                          <tr className="border-t-2 border-slate-300"><td className="py-1.5 font-semibold">Total All-In Cost</td><td className="py-1.5 text-right font-mono font-bold">{fmt(sellResults.totalAllInCost)}</td></tr>

                          <tr className="border-t-2 border-slate-400"><td className="py-2 font-bold">Net Profit</td><td className={`py-2 text-right font-mono font-bold ${sellResults.netProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(sellResults.netProfit)}</td></tr>
                        </tbody>
                      </table>
                    </Card>

                    {sellResults.netProfit < 0 && sellResults.salePrice > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-3 flex gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-red-800">
                          This sale shows a <strong>loss</strong>. Break-even sale price is <strong className="font-mono">{fmt(sellResults.breakEvenSale)}</strong>.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {!rentResults && (
                      <Card className="p-6 text-center">
                        <Banknote className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                        <h3 className="text-sm font-semibold text-slate-900 mb-1">Rental Analysis</h3>
                        <p className="text-xs text-slate-500">Fill in the inputs on the left, then press <strong>Calculate Rental Analysis</strong> to see Year-1 NOI, cap rate, DCR, and a 10-year cash flow projection.</p>
                      </Card>
                    )}

                    {rentResults && (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <KpiTile label="Y1 NOI" value={fmt(rentResults.summary?.year1_noi)} color="emerald" />
                          <KpiTile label="Y1 Cash Flow" value={fmt(rentResults.summary?.year1_cash_flow)} color={(rentResults.summary?.year1_cash_flow || 0) >= 0 ? 'emerald' : 'red'} />
                          <KpiTile label="DCR" value={rentResults.summary?.year1_dcr ?? '—'} color={(parseFloat(rentResults.summary?.year1_dcr) || 0) >= 1.2 ? 'emerald' : 'amber'} sub="Bank target ≥ 1.20x" />
                          <KpiTile label="Cap Rate" value={fmtPct(rentResults.summary?.cap_rate_percent)} color="blue" />
                          <KpiTile label="Cash-on-Cash" value={fmtPct(rentResults.summary?.cash_on_cash_return_percent)} color="blue" />
                          <KpiTile label="10-Yr Total CF" value={fmt(rentResults.summary?.total_10yr_cash_flow)} color="amber" />
                        </div>

                        {rentResults.annual_breakdown && rentResults.annual_breakdown.length > 0 && (
                          <Card className="p-3">
                            <h3 className="text-sm font-semibold mb-2">10-Year Projection</h3>
                            <div className="overflow-x-auto">
                              <table className="w-full text-[10px]">
                                <thead>
                                  <tr className="border-b-2 border-slate-300 text-slate-600">
                                    <th className="py-1 px-1 text-left">Yr</th>
                                    <th className="py-1 px-1 text-right">Income</th>
                                    <th className="py-1 px-1 text-right">OpEx</th>
                                    <th className="py-1 px-1 text-right">NOI</th>
                                    <th className="py-1 px-1 text-right">Debt</th>
                                    <th className="py-1 px-1 text-right">CF</th>
                                  </tr>
                                </thead>
                                <tbody className="font-mono">
                                  {rentResults.annual_breakdown.slice(0, 10).map((y) => (
                                    <tr key={y.year} className="border-b border-slate-100">
                                      <td className="py-1 px-1 font-bold">{y.year}</td>
                                      <td className="py-1 px-1 text-right">{fmt(y.gross_rental_income)}</td>
                                      <td className="py-1 px-1 text-right text-red-700">−{fmt(y.total_operating_expenses)}</td>
                                      <td className="py-1 px-1 text-right">{fmt(y.noi)}</td>
                                      <td className="py-1 px-1 text-right text-red-700">−{fmt(y.total_debt_service)}</td>
                                      <td className={`py-1 px-1 text-right font-bold ${y.cash_flow >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(y.cash_flow)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Card>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default FinancialAnalysis;
